import React from 'react';
import { Card, Statistic, Progress, Row, Col, Space, Typography } from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { useAttendanceSummary } from '../hooks/useAttendanceSummary';
import { formatAttendanceDate } from './formatAttendanceDate';

const { Title } = Typography;

interface AttendanceSummaryProps {
  studentId: number;
  startDate?: string;
  endDate?: string;
}

const AttendanceSummary: React.FC<AttendanceSummaryProps> = ({
  studentId,
  startDate,
  endDate,
}) => {
  const { data, isLoading, error } = useAttendanceSummary({
    studentId,
    startDate,
    endDate,
  });

  if (error) {
    return (
      <Card title="Attendance Summary">
        <Typography.Text type="danger">
          Error loading summary: {error.message}
        </Typography.Text>
      </Card>
    );
  }

  const summary = data || {
    studentId,
    totalDays: 0,
    attendedDays: 0,
    attendancePercentage: 0,
    recentAttendance: [],
  };

  const getProgressColor = (percentage: number) => {
    if (percentage >= 90) return '#52c41a'; // green
    if (percentage >= 80) return '#faad14'; // orange
    if (percentage >= 70) return '#fa8c16'; // dark orange
    return '#f5222d'; // red
  };

  return (
    <Card
      title={
        <Space>
          <CheckCircleOutlined />
          Attendance Summary
        </Space>
      }
      loading={isLoading}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Attendance Percentage */}
        <div style={{ textAlign: 'center' }}>
          <Progress
            type="circle"
            percent={summary.attendancePercentage}
            format={(percent) => `${percent}%`}
            strokeColor={getProgressColor(summary.attendancePercentage)}
            size={120}
          />
          <div style={{ marginTop: '12px' }}>
            <Title level={5} style={{ margin: 0 }}>
              Attendance Rate
            </Title>
          </div>
        </div>

        {/* Statistics */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Statistic
              title="Days Attended"
              value={summary.attendedDays}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Total Days"
              value={summary.totalDays}
              prefix={<CalendarOutlined style={{ color: '#1890ff' }} />}
            />
          </Col>
        </Row>

        {/* Recent Activity Preview */}
        {summary.recentAttendance.length > 0 && (
          <div>
            <Title level={5} style={{ marginBottom: '12px' }}>
              <ClockCircleOutlined style={{ marginRight: '8px' }} />
              Recent Activity
            </Title>
            <div
              style={{
                maxHeight: '150px',
                overflowY: 'auto',
                border: '1px solid #f0f0f0',
                borderRadius: '6px',
                padding: '8px',
              }}
            >
              {summary.recentAttendance.slice(0, 5).map((record, index) => (
                <div
                  key={index}
                  style={{
                    padding: '8px 0',
                    borderBottom:
                      index < summary.recentAttendance.length - 1
                        ? '1px solid #f0f0f0'
                        : 'none',
                  }}
                >
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {formatAttendanceDate(record.date)}
                  </div>
                  <div style={{ fontSize: '14px' }}>
                    {record.signInTime && (
                      <span style={{ color: '#52c41a' }}>
                        In:{' '}
                        {new Date(record.signInTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                    {record.signInTime && record.signOutTime && ' • '}
                    {record.signOutTime && (
                      <span style={{ color: '#fa8c16' }}>
                        Out:{' '}
                        {new Date(record.signOutTime).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Performance Indicator */}
        <div
          style={{
            padding: '12px',
            backgroundColor:
              summary.attendancePercentage >= 90
                ? '#f6ffed'
                : summary.attendancePercentage >= 80
                  ? '#fff7e6'
                  : '#fff2f0',
            border: `1px solid ${
              summary.attendancePercentage >= 90
                ? '#b7eb8f'
                : summary.attendancePercentage >= 80
                  ? '#ffd591'
                  : '#ffb3b3'
            }`,
            borderRadius: '6px',
            textAlign: 'center',
          }}
        >
          <Typography.Text
            style={{
              color:
                summary.attendancePercentage >= 90
                  ? '#52c41a'
                  : summary.attendancePercentage >= 80
                    ? '#fa8c16'
                    : '#f5222d',
              fontWeight: 'bold',
            }}
          >
            {summary.attendancePercentage >= 90
              ? 'Excellent Attendance!'
              : summary.attendancePercentage >= 80
                ? 'Good Attendance'
                : summary.attendancePercentage >= 70
                  ? 'Needs Improvement'
                  : 'Poor Attendance'}
          </Typography.Text>
        </div>
      </Space>
    </Card>
  );
};

export default AttendanceSummary;
